import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, memo } from "react";
import { holidayColumnWashSx } from "@/components/team/Holidays/schedule/holidayTints";
import { usePageScheduleHolidays } from "@/components/team/Holidays/schedule/scheduleHolidaysContext";
import { ALPHA, NEUTRAL } from "@/theme/tokens";
import type { DayItem } from "@/utils/models";
import { CountBadge } from "./CountBadge";
import { DAY_WIDTH } from "./consts";
import { type ComputedLocation, type RoleCount, weightedActual } from "./useWeeklyGapView";

/** Exported so other surfaces showing per-location role breakdowns (e.g. RequiredBreakdownPopover) stay in sync with this row's rendering. */
export const RoleCountBadges: FC<{ roleCounts: RoleCount[] }> = memo(({ roleCounts }) => (
	<Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
		{roleCounts.map((rc) => (
			<Box key={rc.role} sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
				<Typography component="span" sx={(theme) => ({ fontSize: "10px", color: theme.palette.primary.main, fontWeight: 500 })}>
					{rc.role}
				</Typography>
				<CountBadge actual={rc.actual} required={rc.required} />
			</Box>
		))}
	</Box>
));

export const LocationHeaderRow: FC<{ cl: ComputedLocation; weekViewData: DayItem[] }> = memo(({ cl, weekViewData }) => {
	const theme = useTheme();
	const { holidayOn } = usePageScheduleHolidays();
	const displayName = cl.locationShortName || cl.locationName;
	return (
		<Box
			data-testid={`weekly-location-header-${cl.locationName}`}
			sx={{ display: "flex", alignItems: "center", bgcolor: alpha(theme.palette.primary.main, ALPHA.SHADOW_MEDIUM), border: `1px solid ${NEUTRAL[400]}` }}
		>
			{weekViewData.map((dayItem, dayIdx) => {
				const { required, isBlocked, nurses, roleCounts } = cl.dayData[dayIdx];
				const actual = weightedActual(nurses);
				return (
					<Box
						key={`locationHeaderRowDayItem-${dayItem.date.toISO()}`}
						data-testid={`weekly-location-header-day-${dayIdx}`}
						sx={{
							display: "flex",
							alignItems: "center",
							width: DAY_WIDTH,
							minWidth: DAY_WIDTH,
							px: 1,
							gap: 1,
							borderLeft: dayIdx > 0 ? `1px solid ${NEUTRAL[500]}` : undefined,
							transition: "background-color 0.12s ease",
							...(holidayOn(dayItem.date)?.applied && holidayColumnWashSx(theme)),
						}}
					>
						{dayIdx === 0 && <Box>{displayName || "No Location"}</Box>}
						{!isBlocked && (roleCounts ? <RoleCountBadges roleCounts={roleCounts} /> : <CountBadge actual={actual} required={required} />)}
					</Box>
				);
			})}
		</Box>
	);
});
